/**
 * What each body in the picture is wearing, as two colours.
 *
 * public/scoring.js keeps a box on the same player through a crossing partly by
 * the colour of their kit. That file is pure and cannot see pixels, so this one
 * reads them and hands the colours across.
 *
 * WHAT THIS IS NOT. It is not recognition of a person. It reads the colour of a
 * shirt and a pair of shorts, which is a fact about clothing: take the shirt
 * off and the app no longer knows you, put it on somebody else and it thinks
 * they are you. Nothing about a face, a body shape, a gait or a height is read
 * here, and nothing is ever stored. The colours live in the tracker's memory
 * for the length of one run and are gone when it ends. Most of the people this
 * camera sees are children, and that is the line that was drawn for them.
 *
 * COST. The frame is drawn once, very small, and every body samples from that
 * one copy. Reading pixels straight off a full size video for ten bodies would
 * cost more than the pose model does.
 */

const W = 96;                       // the working copy is 96 pixels wide. Colour survives that; detail is not wanted
let canvas = null, g = null, lastW = 0, lastH = 0;

// The frame can be the <video> on the page, or an ImageBitmap handed to the
// tracking worker (public/js/track-worker.js).
function surface(video) {
  const vw = video.videoWidth || video.displayWidth || video.width || 0, vh = video.videoHeight || video.displayHeight || video.height || 0;
  if (!vw || !vh) return null;
  const h = Math.max(8, Math.round(W * vh / vw));
  if (!canvas) {
    canvas = typeof OffscreenCanvas === 'function' ? new OffscreenCanvas(W, h) : Object.assign(document.createElement('canvas'), { width: W, height: h });
    // willReadFrequently keeps the copy on the CPU. Without it every read is a
    // round trip to the GPU, and that stall is what would make this slow.
    g = canvas.getContext('2d', { willReadFrequently: true });
  }
  if (lastW !== W || lastH !== h) { canvas.width = W; canvas.height = h; lastW = W; lastH = h; }
  return { h };
}

const seen = (p) => p && typeof p.x === 'number' && (p.visibility ?? 1) > 0.5;

/** The middle of a few landmarks, or null when any of them is out of sight. */
function middle(lm, ids) {
  let x = 0, y = 0;
  for (const i of ids) { if (!seen(lm[i])) return null; x += lm[i].x; y += lm[i].y; }
  return { x: x / ids.length, y: y / ids.length };
}

/** Mean colour of a small patch around a point given in 0..1 frame units. */
function patch(data, h, at, size = 2) {
  if (!at) return null;
  const cx = Math.round(at.x * (W - 1)), cy = Math.round(at.y * (h - 1));
  let r = 0, gr = 0, b = 0, n = 0;
  for (let y = cy - size; y <= cy + size; y++) {
    if (y < 0 || y >= h) continue;
    for (let x = cx - size; x <= cx + size; x++) {
      if (x < 0 || x >= W) continue;
      const i = (y * W + x) * 4;
      r += data[i]; gr += data[i + 1]; b += data[i + 2]; n++;
    }
  }
  return n ? [r / n, gr / n, b / n] : null;
}

/**
 * One outfit per pose, in the same order as `poses`, or null where a body's
 * torso or legs could not be seen. Never throws: a frame that cannot be read
 * just means the tracker runs on movement alone for that frame.
 */
export function sampleLooks(video, poses) {
  try {
    if (!video || !Array.isArray(poses) || !poses.length) return null;
    const s = surface(video);
    if (!s) return null;
    g.drawImage(video, 0, 0, W, s.h);
    const { data } = g.getImageData(0, 0, W, s.h);
    return poses.map((lm) => {
      if (!Array.isArray(lm) || lm.length < 29) return null;
      // Chest: between the shoulders and the hips, which is shirt on anybody.
      const top = patch(data, s.h, middle(lm, [11, 12, 23, 24]));
      // Thighs: between hip and knee on each side, which is shorts or trousers.
      const left = middle(lm, [23, 25]), right = middle(lm, [24, 26]);
      const leg = left && right ? { x: (left.x + right.x) / 2, y: (left.y + right.y) / 2 } : (left || right);
      const bottom = patch(data, s.h, leg);
      return top || bottom ? { top, bottom } : null;
    });
  } catch {
    // A tainted canvas, a video mid teardown, a context that was lost. None of
    // them is worth stopping a run for.
    return null;
  }
}
